/**
 * Keeps the listing the reader was looking at in the same place on Back.
 *
 * Restoring a saved scrollY is not enough. On prod (2026-10-20) Back put the
 * scroll back at the right number, and then the banner ad and the tag pills
 * loaded above the grid and pushed every tile down by about 425px: the reader
 * came back two rows higher than they left. Chrome's scroll anchoring would
 * absorb that; iOS Safari has none.
 *
 * So the first listing on screen and its distance from the top are saved with
 * the scroll position, and after the restore that listing is held at that
 * distance while the page settles. The hold stops the moment the reader
 * touches, scrolls or types, and after `HOLD_MS` regardless.
 */

export interface ScrollAnchor {
  /** The listing id from its /product/<id>/<slug> link. */
  id: string;
  /** Its top, in px from the top of the viewport, when the reader left. */
  top: number;
}

/** A saved anchor read back from storage: anything else is ignored. */
export function isScrollAnchor(value: unknown): value is ScrollAnchor {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && /^\d+$/.test(v.id) && typeof v.top === 'number' && Number.isFinite(v.top);
}

/** How long the page is watched after Back for layout pushing the anchor. */
export const HOLD_MS = 3000;

/** Smaller moves are left alone: sub-pixel rounding, not a layout shift. */
const TOLERANCE_PX = 4;

const PRODUCT_LINK = 'a[href*="/product/"]';

function listingIdOf(link: HTMLAnchorElement): string | null {
  // Only links PRODUCT_LINK matched come here, so each has an href.
  const match = /\/product\/(\d+)/.exec(link.getAttribute('href'));
  return match ? match[1] : null;
}

/** The first listing whose picture starts on screen, and where it starts. */
export function captureScrollAnchor(doc: Document = document, win: Window = window): ScrollAnchor | null {
  let best: ScrollAnchor | null = null;
  for (const link of Array.from(doc.querySelectorAll<HTMLAnchorElement>(PRODUCT_LINK))) {
    const rect = link.getBoundingClientRect();
    if (rect.width === 0 || rect.top < 0 || rect.top >= win.innerHeight) continue;
    const id = listingIdOf(link);
    if (!id) continue;
    if (!best || rect.top < best.top) best = { id, top: rect.top };
  }
  return best;
}

/**
 * Where the anchor listing is now, or null when it is not on the page.
 *
 * The same listing can sit in two rows (a category row and "Great Finds Under
 * $100", say). Taking the first copy on the page held the wrong one: Back on
 * prod (2026-10-26) jumped the reader 1,050px up to the higher row. So the copy
 * nearest where the anchor should be is held, and none further than a screen
 * away: a copy that far off is another row, not the page having shifted.
 */
function anchorTop(anchor: ScrollAnchor, doc: Document, win: Window): number | null {
  let best: number | null = null;
  for (const link of Array.from(doc.querySelectorAll<HTMLAnchorElement>(PRODUCT_LINK))) {
    if (listingIdOf(link) !== anchor.id) continue;
    const rect = link.getBoundingClientRect();
    if (rect.width === 0) continue;
    const drift = Math.abs(rect.top - anchor.top);
    if (drift > win.innerHeight) continue;
    if (best === null || drift < Math.abs(best - anchor.top)) best = rect.top;
  }
  return best;
}

/**
 * How far the anchor listing is from where the reader left it, in px (positive:
 * lower on screen). Null when no copy of it is within a screen of that place.
 */
export function anchorDrift(anchor: ScrollAnchor, doc: Document = document, win: Window = window): number | null {
  const top = anchorTop(anchor, doc, win);
  return top === null ? null : top - anchor.top;
}

/**
 * Hold `anchor` at its saved distance from the top for `ms`. Returns a
 * function that stops the hold early.
 */
export function holdScrollAnchor(anchor: ScrollAnchor, ms: number = HOLD_MS, win: Window = window): () => void {
  const doc = win.document;
  // The reader has taken over: their scroll is not ours to undo.
  const READER_INPUT = ['touchstart', 'wheel', 'keydown', 'mousedown'] as const;
  let stopped = false;
  let timer = 0;
  let deadline = 0;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    win.clearInterval(timer);
    win.clearTimeout(deadline);
    for (const type of READER_INPUT) win.removeEventListener(type, stop, true);
  };

  // Runs only from the interval, which stop() clears.
  const correct = () => {
    const top = anchorTop(anchor, doc, win);
    if (top === null) return;
    const drift = top - anchor.top;
    if (Math.abs(drift) > TOLERANCE_PX) win.scrollBy(0, drift);
  };

  for (const type of READER_INPUT) win.addEventListener(type, stop, { capture: true, passive: true });

  timer = win.setInterval(correct, 100);
  deadline = win.setTimeout(stop, ms);
  correct();
  return stop;
}
